(() => {
  if (window.LimitlessIntroMotion) { window.LimitlessIntroMotion.init(document); return; }
  const states = new Map();
  let userPaused = false;
  const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const now = () => window.performance.now();
  const duration = (value, fallback, min, max) => Math.max(min, Math.min(max, Number(value) || fallback));
  const canRun = state => Boolean(observer) && !motion?.matches && state.visible && !state.userPaused && !document.hidden && state.root.isConnected && !state.root.closest('[hidden]');
  const stopClock = state => {
    if (state.timer !== null) {
      window.clearTimeout(state.timer);
      state.timer = null;
      state.remaining = Math.max(0, state.remaining - (now() - state.started));
    }
    state.animations.forEach(animation => animation.pause());
  };
  const startClock = state => {
    if (!canRun(state) || state.timer !== null || !state.callback) return;
    state.started = now();
    state.animations.forEach(animation => animation.play());
    state.timer = window.setTimeout(() => {
      state.timer = null;
      state.remaining = 0;
      const callback = state.callback;
      state.callback = null;
      callback();
    }, state.remaining);
  };
  const schedule = (state, callback, delay) => {
    state.callback = callback;
    state.remaining = delay;
    startClock(state);
  };
  const cancelAnimations = state => {
    state.animations.forEach(animation => animation.cancel());
    state.animations = [];
    state.widthAnimation = null;
  };
  const widthFor = (state, value) => state.widths.get(value) || '3.6em';
  const widthFrames = state => [{width: widthFor(state, state.value)}, {width: widthFor(state, state.incomingValue)}];
  const measure = state => {
    const fontSize = parseFloat(window.getComputedStyle(state.slot).fontSize);
    state.probes.forEach((probe, value) => {
      const width = probe.getBoundingClientRect().width;
      if (width > 0) state.widths.set(value, fontSize > 0 ? `${width / fontSize}em` : `${width}px`);
    });
    const transitioning = state.root.dataset.llIntroPhase === 'transition';
    state.slot.style.width = widthFor(state, transitioning ? state.incomingValue : state.value);
    if (transitioning) state.widthAnimation?.effect?.setKeyframes(widthFrames(state));
  };
  const queueMeasure = state => {
    if (state.measureFrame !== null) return;
    state.measureFrame = window.requestAnimationFrame(() => {
      state.measureFrame = null;
      if (states.has(state.root)) measure(state);
    });
  };
  const render = (state, layer, value) => {
    layer.replaceChildren(value === null ? state.logo.content.cloneNode(true) : document.createTextNode(value));
    layer.dataset.llIntroKind = value === null ? 'logo' : 'word';
  };
  const reset = state => {
    stopClock(state);
    cancelAnimations(state);
    state.callback = null;
    state.active = 0;
    state.wordIndex = 0;
    state.wordAppearances = 0;
    state.value = null;
    state.layers.forEach((layer, index) => { render(state, layer, null); layer.hidden = index !== 0; });
    state.root.dataset.llIntroPhase = 'hold';
    measure(state);
    schedule(state, () => advance(state), state.logoHold);
  };
  const sharePause = state => {
    if (!state.sharedScope) return;
    state.sharedScope.dataset.llSharedPaused = String(state.userPaused);
    window.LimitlessHeroMotion?.setPaused?.(state.sharedScope, state.userPaused);
    window.LimitlessLogoMotion?.setPaused?.(state.sharedScope, state.userPaused);
    window.LimitlessServiceRotation?.setPaused?.(state.sharedScope, state.userPaused);
  };
  const paint = state => {
    const available = (Boolean(observer) || Boolean(state.sharedScope)) && !motion?.matches;
    document.querySelectorAll('[data-ll-intro-toggle]').forEach(button => {
      button.hidden = !available;
      button.textContent = state.userPaused ? button.dataset.resumeLabel : button.dataset.pauseLabel;
      button.setAttribute('aria-pressed', String(state.userPaused));
    });
    state.root.dataset.llIntroState = available ? (canRun(state) ? 'running' : 'paused') : 'static';
    if (canRun(state)) startClock(state); else stopClock(state);
  };
  const advance = state => {
    const isLogo = state.value !== null && state.wordAppearances % 4 === 0;
    const next = isLogo ? null : state.words[state.wordIndex];
    if (next !== null) {
      state.wordIndex = (state.wordIndex + 1) % state.words.length;
      state.wordAppearances += 1;
    }
    const outgoing = state.layers[state.active];
    const nextActive = 1 - state.active;
    const incoming = state.layers[nextActive];
    render(state, incoming, next);
    incoming.hidden = false;
    state.incomingValue = next;
    state.root.dataset.llIntroPhase = 'transition';
    const finish = () => {
      outgoing.hidden = true;
      cancelAnimations(state);
      state.active = nextActive;
      state.value = next;
      state.slot.style.width = widthFor(state, next);
      state.root.dataset.llIntroPhase = 'hold';
      schedule(state, () => advance(state), next === null ? state.logoHold : state.wordHold);
      paint(state);
    };
    if (typeof incoming.animate === 'function') {
      const options = {duration: state.transition, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both'};
      state.widthAnimation = state.slot.animate(widthFrames(state), options);
      state.animations = [
        state.widthAnimation,
        outgoing.animate([{transform: 'translateY(0) rotateX(0deg)', opacity: 1}, {transform: 'translateY(-.42em) rotateX(12deg)', opacity: 0}], options),
        incoming.animate([{transform: 'translateY(.42em) rotateX(-12deg)', opacity: 0}, {transform: 'translateY(0) rotateX(0deg)', opacity: 1}], options)
      ];
      schedule(state, finish, state.transition);
      paint(state);
    } else {
      finish();
    }
  };
  const observer = typeof window.IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = states.get(entry.target);
      if (state) { state.visible = entry.isIntersecting && entry.intersectionRatio > 0; if (state.visible) measure(state); paint(state); }
    });
  }, {threshold: [0, 0.01]}) : null;
  const resizer = typeof window.ResizeObserver === 'function' ? new ResizeObserver(entries => {
    entries.forEach(entry => { const state = states.get(entry.target); if (state) queueMeasure(state); });
  }) : null;
  const init = (root = document) => {
    const nodes = [...(root.matches?.('[data-ll-intro]') ? [root] : []), ...root.querySelectorAll('[data-ll-intro]')];
    nodes.forEach(node => {
      if (states.has(node)) return;
      const sharedScope = node.closest('[data-ll-shared-motion]');
      const logo = node.querySelector('[data-ll-intro-logo]');
      const layers = [...node.querySelectorAll('[data-ll-intro-layer]')];
      const slot = node.querySelector('[data-ll-intro-slot]');
      const wordProbes = [...node.querySelectorAll('[data-ll-intro-word]')].filter(probe => probe.textContent.trim());
      const words = wordProbes.map(probe => probe.textContent.trim());
      const probes = new Map(wordProbes.map(probe => [probe.textContent.trim(), probe]));
      const logoProbe = node.querySelector('.ll-intro__logo-size');
      if (!logo || !slot || !logoProbe || layers.length !== 2 || !words.length) return;
      probes.set(null, logoProbe);
      const controller = new AbortController();
      const state = {root: node, logo, layers, words, slot, probes, widths: new Map(), widthAnimation: null, measureFrame: null, controller, sharedScope, timer: null, callback: null, remaining: 0, started: 0, animations: [], visible: false, userPaused: userPaused || sharedScope?.dataset.llSharedPaused === 'true',
        wordHold: duration(node.dataset.llIntroWordHold, 1000, 1000, 5000), logoHold: duration(node.dataset.llIntroLogoHold, 2000, 1000, 6000), transition: duration(node.dataset.llIntroTransition, 400, 200, 800)};
      states.set(node, state);
      reset(state);
      sharePause(state);
      paint(state);
      observer?.observe(node);
      resizer?.observe(node);
    });
  };
  const destroy = (root = document) => {
    states.forEach((state, node) => {
      if (root === document || root === node || root.contains(node)) {
        stopClock(state);
        cancelAnimations(state);
        state.callback = null;
        state.controller.abort();
        observer?.unobserve(node);
        resizer?.unobserve(node);
        if (state.measureFrame !== null) window.cancelAnimationFrame(state.measureFrame);
        state.slot.style.removeProperty('width');
        render(state, state.layers[0], null);
        state.layers[0].hidden = false;
        state.layers[1].hidden = true;
        node.dataset.llIntroState = 'static';
        node.dataset.llIntroPhase = 'hold';
        states.delete(node);
      }
    });
  };
  document.addEventListener('click', event => {
    if (!event.target.closest?.('[data-ll-intro-toggle]') || !states.size) return;
    userPaused = ![...states.values()].every(state => state.userPaused);
    states.forEach(state => { state.userPaused = userPaused; sharePause(state); paint(state); });
  });
  document.addEventListener('visibilitychange', () => states.forEach(paint));
  const refreshSizes = () => states.forEach(queueMeasure);
  window.addEventListener('resize', refreshSizes);
  document.fonts?.ready?.then(refreshSizes);
  document.fonts?.addEventListener?.('loadingdone', refreshSizes);
  document.addEventListener('shopify:section:load', event => { init(event.target); states.forEach(paint); });
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  const preferenceChanged = () => states.forEach(state => { reset(state); paint(state); });
  if (motion?.addEventListener) motion.addEventListener('change', preferenceChanged); else motion?.addListener?.(preferenceChanged);
  window.LimitlessIntroMotion = {init, destroy};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true}); else init();
})();
